import { adminApi } from '@/config/axios.config';
import {
  Tenant,
  CreateTenantDto,
  UpdateTenantDto,
  TenantFilters,
  PaginatedResponse,
} from '../../shared/types/tenant';

// Re-exportar tipos para compatibilidad
export type {
  Tenant,
  CreateTenantDto,
  UpdateTenantDto,
  TenantFilters,
  PaginatedResponse,
  TenantStats,
} from '../../shared/types/tenant';

// API de Tenants
export const tenantsApiService = {
  // Obtener detalles de un tenant específico
  getTenantDetails: async (tenantId: string): Promise<Tenant> => {
    const response = await adminApi.get(`/admin/tenants/${tenantId}`);
    
    const backendData = response.data.data;
    
    // Mapear los datos del backend a la estructura esperada por el frontend
    const mappedTenant = {
      id: backendData.id,
      name: backendData.name,
      subdomain: backendData.subdomain || '',
      email: backendData.email,
      plan: backendData.plan,
      status: backendData.status,
      registrationSource: backendData.registrationSource,
      usersCount: backendData.usersCount || 0,
      propertiesCount: backendData.propertiesCount || 0,
      createdAt: backendData.createdAt,
      expiresAt: backendData.expiresAt || backendData.subscriptionEndsAt || backendData.trialEndsAt,
      contactPerson: backendData.contactPerson || '',
      phone: backendData.phone || '',
      address: backendData.address || '',
      updatedAt: backendData.updatedAt,
      planLimits: backendData.planLimits, // Incluir límites del plan
      // Rol del usuario administrador del tenant - manejar null, undefined y string vacío
      adminRole: backendData.adminRole && String(backendData.adminRole).trim() !== '' ? String(backendData.adminRole) : undefined,
    };
    
    return mappedTenant;
  },

  // Obtener todos los tenants con filtros
  getTenants: async (
    filters: TenantFilters = {}
  ): Promise<PaginatedResponse<Tenant>> => {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.plan) params.append('plan', filters.plan);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await adminApi.get('/admin/tenants', { params });

    // El backend devuelve { success: true, data: [...], pagination: {...} }
    const backendData = response.data.data; // Array directo de tenants
    const paginationData = response.data.pagination; // Datos de paginación
    
    // Verificar que los datos estén en el formato esperado
    if (!Array.isArray(backendData)) {
      throw new Error('Formato de datos inesperado del backend');
    }
    
    // Mapear los datos del backend a la estructura esperada por el frontend
    const mappedTenants = backendData.map((tenant: any, index: number) => {
      const mappedTenant = {
        id: tenant.id,
        name: tenant.name,
        subdomain: tenant.subdomain || '',
        email: tenant.email,
        plan: tenant.plan || 'Sin Plan', // Asegurar que siempre haya un valor
        status: tenant.status,
        registrationSource: tenant.registrationSource,
        usersCount: tenant.usersCount || 0,
        propertiesCount: tenant.propertiesCount || 0,
        createdAt: tenant.createdAt,
        expiresAt: tenant.expiresAt || tenant.subscriptionEndsAt || tenant.trialEndsAt,
        contactPerson: tenant.contactPerson || tenant.name, // Usar contactPerson si existe, sino name
        phone: tenant.phone || '',
        address: tenant.address || '',
        updatedAt: tenant.updatedAt,
        planLimits: tenant.planLimits, // Incluir límites del plan
        // Rol del usuario administrador del tenant - manejar null, undefined y string vacío
        adminRole: tenant.adminRole && String(tenant.adminRole).trim() !== '' ? String(tenant.adminRole) : undefined,
      };

      return mappedTenant;
    });
    
    return {
      data: mappedTenants,
      page: paginationData.page,
      limit: paginationData.limit,
      total: paginationData.total,
      totalPages: paginationData.totalPages,
    };
  },

  // Obtener un tenant por ID
  getTenant: async (id: string): Promise<Tenant> => {
    const response = await adminApi.get(`/admin/tenants/${id}`);
    const backendData = response.data.data || response.data;

    // Mapear los datos del backend a la estructura esperada por el frontend
    const mappedTenant: Tenant & { subscriptionPolicyId?: string; planId?: string } = {
      id: backendData.id,
      name: backendData.name,
      subdomain: backendData.subdomain || '',
      email: backendData.email,
      plan: backendData.plan,
      status: backendData.status,
      usersCount: backendData.usersCount || 0,
      propertiesCount: backendData.propertiesCount || 0,
      createdAt: backendData.createdAt,
      expiresAt: backendData.expiresAt || backendData.updatedAt,
      contactPerson: backendData.contactPerson || '',
      phone: backendData.phone || '',
      address: backendData.address || '',
      country: backendData.settings?.country || backendData.country || 'República Dominicana',
      state: backendData.settings?.state || backendData.state || '',
      city: backendData.settings?.city || backendData.city || '',
      postalCode: backendData.settings?.postalCode || backendData.postalCode || '',
      updatedAt: backendData.updatedAt,
      adminRole: backendData.adminRole && String(backendData.adminRole).trim() !== '' ? String(backendData.adminRole) : undefined,
      subscriptionPolicyId: backendData.subscriptionPolicyId || backendData.settings?.subscriptionPolicyId,
      planId: backendData.planId || backendData.settings?.planId,
      commercialName: backendData.settings?.commercialName || backendData.commercialName || '',
      // billingCycle viene de la suscripción (única fuente de verdad)
      // Fallback a settings solo para compatibilidad con datos antiguos
      billingCycle: backendData.billingCycle || backendData.settings?.billingCycle || 'monthly',
    } as Tenant & { subscriptionPolicyId?: string; planId?: string; billingCycle?: 'monthly' | 'yearly' };

    return mappedTenant;
  },

  // Crear un nuevo tenant
  createTenant: async (data: CreateTenantDto): Promise<Tenant> => {
    const response = await adminApi.post('/admin/tenants', data);
    
    const backendData = response.data.data || response.data;
    
    // Mapear los datos del backend a la estructura esperada por el frontend
    const mappedTenant = {
      id: backendData.id,
      name: backendData.name,
      subdomain: backendData.subdomain || '',
      email: backendData.email,
      plan: backendData.plan,
      status: backendData.status,
      usersCount: backendData.usersCount || 0,
      propertiesCount: backendData.propertiesCount || 0,
      createdAt: backendData.createdAt,
      expiresAt: backendData.expiresAt,
      contactPerson: backendData.contactPerson || '',
      phone: backendData.phone || '',
      address: backendData.address || '',
      updatedAt: backendData.updatedAt,
      adminRole: backendData.adminRole, // Rol del usuario administrador del tenant
    };
    
    return mappedTenant;
  },

  // Actualizar un tenant
  updateTenant: async (id: string, data: UpdateTenantDto): Promise<Tenant> => {
    const response = await adminApi.patch(`/admin/tenants/${id}`, data);
    const backendData = response.data.data || response.data;
    
    // Mapear la respuesta del backend a la estructura esperada por el frontend
    const mappedTenant: Tenant = {
      id: backendData.id,
      name: backendData.name,
      subdomain: backendData.subdomain || '',
      email: backendData.email,
      plan: backendData.plan,
      status: backendData.status,
      usersCount: backendData.usersCount || 0,
      propertiesCount: backendData.propertiesCount || 0,
      createdAt: backendData.createdAt,
      expiresAt: backendData.expiresAt || backendData.updatedAt,
      contactPerson: backendData.contactPerson || backendData.name,
      phone: backendData.phone || '',
      address: backendData.address || '',
      updatedAt: backendData.updatedAt,
      adminRole: backendData.adminRole, // Rol del usuario administrador del tenant
    };
    
    return mappedTenant;
  },

  // Verificar restricciones antes de eliminar un tenant
  checkDeleteRestrictions: async (id: string): Promise<{
    canDelete: boolean;
    restrictions: string[];
  }> => {
    try {
      const response = await adminApi.get(`/admin/tenants/${id}/delete-restrictions`);
      return response.data;
    } catch (error: any) {
      // Si el endpoint no existe, devolver restricciones por defecto
      if (error.response?.status === 404) {
        return {
          canDelete: true,
          restrictions: []
        };
      }
      throw error;
    }
  },

  // Eliminar un tenant (soft delete)
  deleteTenant: async (id: string): Promise<void> => {
    // Usar la misma ruta que las otras operaciones para consistencia
    await adminApi.delete(`/admin/tenants/${id}`);
  },

  // Obtener tenants eliminados (papelera)
  getDeletedTenants: async (filters: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<PaginatedResponse<Tenant>> => {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await adminApi.get(`tenants/trash?${params.toString()}`);
    
    // El backend devuelve { success: true, data: { tenants: [...], total, page, limit, totalPages } }
    // o directamente { tenants: [...], total, page, limit, totalPages }
    const backendData = response.data?.data?.tenants || response.data?.tenants || [];

    // Asegurarse de que siempre sea un array
    const data = Array.isArray(backendData) ? backendData : [];

    const mappedTenants = data.map((tenant: any) => ({
      id: tenant.id,
      name: tenant.name,
      subdomain: tenant.subdomain || '',
      email: tenant.email,
      plan: tenant.plan,
      status: tenant.status,
      usersCount: tenant.usersCount || 0,
      propertiesCount: tenant.propertiesCount || 0,
      createdAt: tenant.createdAt,
      expiresAt: tenant.subscriptionEndsAt || tenant.expiresAt || tenant.updatedAt,
      contactPerson: tenant.contactPerson || tenant.name,
      phone: tenant.phone || '',
      address: tenant.address || '',
      updatedAt: tenant.updatedAt,
      deletedAt: tenant.deletedAt,
    }));

    // Extraer paginación de la respuesta (puede estar en response.data.data o response.data)
    const paginationData = response.data?.data || response.data || {};
    
    return {
      data: mappedTenants,
      page: paginationData.page || filters.page || 1,
      limit: paginationData.limit || filters.limit || 10,
      total: paginationData.total || 0,
      totalPages: paginationData.totalPages || 0,
    };
  },

  // Restaurar tenant eliminado
  restoreTenant: async (id: string): Promise<Tenant> => {
    const response = await adminApi.post(`tenants/${id}/restore`);
    const backendData = response.data?.data || response.data;

    return {
      id: backendData.id,
      name: backendData.name,
      subdomain: backendData.subdomain || '',
      email: backendData.email,
      plan: backendData.plan,
      status: backendData.status,
      usersCount: backendData.usersCount || 0,
      propertiesCount: backendData.propertiesCount || 0,
      createdAt: backendData.createdAt,
      expiresAt: backendData.expiresAt || backendData.subscriptionEndsAt,
      contactPerson: backendData.contactPerson || backendData.name,
      phone: backendData.phone || '',
      address: backendData.address || '',
      updatedAt: backendData.updatedAt,
    };
  },

  // Eliminar permanentemente
  permanentDeleteTenant: async (id: string): Promise<void> => {
    await adminApi.delete(`tenants/${id}/permanent`);
  },

  // Cambiar estado de un tenant
  updateTenantStatus: async (
    id: string,
    status: Tenant['status']
  ): Promise<Tenant> => {
    const response = await adminApi.patch(`/admin/tenants/${id}/status`, {
      status,
    });
    
    // Mapear la respuesta del backend para asegurar que tenga la estructura correcta
    const backendTenant = response.data.data || response.data;
    const mappedTenant: Tenant = {
      id: backendTenant.id,
      name: backendTenant.name,
      subdomain: backendTenant.subdomain || '',
      email: backendTenant.email,
      plan: backendTenant.plan,
      status: backendTenant.status,
      usersCount: backendTenant.usersCount || 0,
      propertiesCount: backendTenant.propertiesCount || 0,
      createdAt: backendTenant.createdAt,
      expiresAt: backendTenant.expiresAt || backendTenant.updatedAt,
      contactPerson: backendTenant.contactPerson || backendTenant.name,
      phone: backendTenant.phone || '',
      address: backendTenant.address || '',
      updatedAt: backendTenant.updatedAt,
    };
    
    return mappedTenant;
  },

  // Obtener estadísticas de tenants
  getTenantStats: async (): Promise<{
    total: number;
    active: number;
    inactive: number;
    suspended: number;
    pending: number;
  }> => {
    const response = await adminApi.get('/admin/tenants/stats');
    // El backend devuelve { success: true, data: {...} }
    return response.data.data;
  },

  // Validar disponibilidad de un subdomain
  validateSubdomain: async (subdomain: string, excludeTenantId?: string): Promise<{ available: boolean; message?: string }> => {
    const params: { subdomain: string; excludeTenantId?: string } = { subdomain };
    if (excludeTenantId) {
      params.excludeTenantId = excludeTenantId;
    }
    const response = await adminApi.get('/admin/tenants/validate-subdomain', {
      params,
    });
    // El backend devuelve { success: true, data: { available, message } }
    return response.data.data || response.data;
  },
};

export default tenantsApiService;
